// src/components/LinearDayColumnDOM.jsx
import React, { useMemo, useCallback } from 'react';
import { SCHEDULE_START_MINUTE, SCHEDULE_END_MINUTE, TOTAL_SCHEDULE_MINUTES } from '../config/constants';
import { formatDayWithNumber } from '../utils/helpers.ts';
import ScheduleItemCard from './ScheduleItemCard';

/**
 * Renders a single day's column for the linear schedule view using DOM elements.
 * Uses Flexbox to position the header and content area correctly.
 * Displays items based on time using absolute positioning within the content area.
 * Time markers are now handled by the parent LinearWeekViewDOM component.
 * 
 * Conditional Display Logic:
 * - When filtering by teacher (selectedTeacher is set), teacher names are hidden from cards
 * - When filtering by group (selectedGroups has items), group names are hidden from cards
 * - This reduces redundancy when viewing filtered schedules
 * 
 * Overlapping Items:
 * - Items that overlap in time now share the column width proportionally
 * - Each overlapping item takes 1/n of the available width where n is the number of overlapping items
 */
function LinearDayColumnDOM({ day, items, onOpenModal, user, weekStartDate, selectedTeacher, selectedGroups, overlappingGroups, columnHeight }) {
  // Check user role for potential conditional styling or interactions
  const isAdmin = user?.role === 'Admin';

  // --- Configuration ---
  // Set a fixed height for columns or calculate dynamically
  const dayColumnHeight = columnHeight || '800px'; 

  // Format day information
  const dayInfo = formatDayWithNumber(day, weekStartDate || new Date());

  // --- Calculate Style for a Schedule Item ---
  // Uses useCallback to memoize the function based on dependencies
  const getItemStyle = useCallback((item) => {
    // Basic validation
    if (!item || !item.isTimeValid) return { display: 'none' };

    // Get start and duration in minutes from the time string
    const { startMinutes, durationMinutes } = item.parseTime();
    if (startMinutes === null || durationMinutes === null || durationMinutes <= 0) {
      console.warn(`[${day}] Invalid time/duration for item ${item.id}: ${item.time}`);
      return { display: 'none' };
    }

    // Calculate position relative to the item container's height
    const offsetMinutes = startMinutes - SCHEDULE_START_MINUTE;
    const topPercent = Math.max(0, (offsetMinutes / TOTAL_SCHEDULE_MINUTES) * 100);
    // Ensure height calculation doesn't exceed remaining space
    const heightPercent = Math.min(100 - topPercent, (durationMinutes / TOTAL_SCHEDULE_MINUTES) * 100);

    // Find if this item belongs to an overlapping group
    let width = 'calc(100% - 8px)'; // Default width with padding
    let left = '4px'; // Default left position

    if (overlappingGroups && overlappingGroups.length > 0) {
      // Find the overlapping group this item belongs to
      const overlappingGroup = overlappingGroups.find(group =>
        group.some(groupEntry =>
          groupEntry.id === item.id
        )
      );

      if (overlappingGroup && overlappingGroup.length > 1) {
        // Calculate position within the overlapping group
        const groupSize = overlappingGroup.length;
        const indexInGroup = overlappingGroup.findIndex(groupEntry => groupEntry.id === item.id);

        // Each item takes up 1/groupSize of the width
        const itemWidthPercent = 100 / groupSize;
        width = `calc(${itemWidthPercent}% - 4px)`; // Subtract padding
        left = `calc(${indexInGroup * itemWidthPercent}% + 2px)`; // Add small offset for visual separation
      }
    }

    // Return CSS properties for absolute positioning
    return {
      position: 'absolute',
      top: `${topPercent}%`,
      height: `${heightPercent}%`,
      left: left,
      width: width,
      zIndex: 10, // z-index for items
      minHeight: '20px', // Prevent text clipping on very short duration items
      durationMinutes, // Add duration for layout decisions
    };
  }, [day, overlappingGroups]); // Dependencies: recalculate if day or overlappingGroups change

  // --- Render Component ---
  return (
    // Main column container: Use Flexbox for vertical layout with improved styling
    <div className="relative bg-white flex flex-col border-r border-gray-200 last:border-r-0" style={{ height: dayColumnHeight }}>

      {/* Day Header: Enhanced styling with short name and day number */}
      <div className="bg-gradient-to-b from-white to-gray-100 border-b-2 border-gray-300 sticky top-0 z-30 flex-shrink-0 h-[40px] md:h-[68px] py-1 md:py-3 px-1 md:px-2 flex items-center justify-center box-border">
        <div className="flex flex-row md:flex-col items-baseline md:items-center justify-center gap-1 md:gap-0 text-center w-full">
          <div className="text-xs md:text-sm font-semibold text-gray-700 uppercase tracking-wide">
            {dayInfo.shortName}
          </div>
          <div className="text-sm md:text-lg font-bold text-gray-900 leading-tight">
            {dayInfo.dayNumber}
          </div>
        </div>
      </div>

      {/* Schedule Items Container: Relative positioning context, takes full height, no left padding since time markers are external */}
      <div className="relative flex-grow min-h-0 px-1">
        {/* Map through items for the day */}
        {items.map(item => {
          // Calculate style for the item
          const style = getItemStyle(item);
          // Skip rendering if style indicates 'none' (e.g., invalid time)
          if (style.display === 'none') return null;

          // Style object for the card (omit non-CSS helpers)
          const { durationMinutes, ...itemStyle } = style;

          const formatGroupNames = (groups) => (groups || []).map(g => typeof g === 'object' ? (g.name || g.group_code || g.id) : g).join(', ');

          return (
            <ScheduleItemCard
              key={item.id}
              entry={item}
              isAdmin={isAdmin}
              style={itemStyle}
              formatGroupNames={formatGroupNames}
              onClick={() => onOpenModal(item)}
              onConflictIconClick={(e) => { e.stopPropagation(); }}
            />
          );
        })}
      </div>

    </div>
  );
}

export default LinearDayColumnDOM;